import type { Client } from 'urql';

import type {
  ExpandedSelectionListsQuery,
  SelectionListsQuery,
} from '@revelio/data-access';
import {
  EXPANDED_SELECTION_LISTS,
  REFERENCE_LISTS_QUERY,
  SELECTION_LISTS,
} from '@revelio/data-access';

import type { AllSelectionLists, GqlSelectionLists } from '../types';
import { normalizeReferenceLists } from '../utils/referenceLists';

type ReferenceListsPartial = NonNullable<
  ReturnType<typeof normalizeReferenceLists>
>;

const mergeGqlSelectionListsWithReferenceLists = (
  base: GqlSelectionLists,
  referenceLists: ReferenceListsPartial | null
): GqlSelectionLists => ({ ...base, ...(referenceLists ?? {}) });

const mergeAllSelectionListsWithReferenceLists = (
  base: AllSelectionLists,
  referenceLists: ReferenceListsPartial | null
): AllSelectionLists => ({ ...base, ...(referenceLists ?? {}) });

const legacySelectionListToGqlSelectionLists = (
  selectionList: NonNullable<SelectionListsQuery['selectionList']>
): GqlSelectionLists => {
  const { __typename: _unused, ...standard } = selectionList;
  return standard;
};

type ExpandedSelectionListData = NonNullable<
  ExpandedSelectionListsQuery['selectionList']
>;
type ExpandedSelectionListsWithMsa = ExpandedSelectionListData & {
  msa?: ExpandedSelectionListData['metro_area'];
};
type LabelableSelectionListItem = {
  longName?: string | null;
};

const logReferenceListsError = (error: unknown) => {
  console.error('Error fetching reference lists:', error);
};

const addLabel = <T extends LabelableSelectionListItem>(
  list: (T | null)[] | null | undefined
): ((T & { label: string | null | undefined }) | null)[] | null | undefined =>
  Array.isArray(list)
    ? list.map((item) =>
        item === null
          ? null
          : {
              ...item,
              label: item.longName,
            }
      )
    : list;

const normalizeExpandedSelectionLists = (
  selectionList: ExpandedSelectionListData
): ExpandedSelectionListsWithMsa => {
  const metroArea = addLabel(selectionList.metro_area);

  return {
    ...selectionList,
    region: addLabel(selectionList.region),
    country: addLabel(selectionList.country),
    state: addLabel(selectionList.state),
    metro_area: metroArea,
    msa: metroArea,
    city: addLabel(selectionList.city),
    ethnicity: addLabel(selectionList.ethnicity),
    gender: addLabel(selectionList.gender),
    highest_degree: addLabel(selectionList.highest_degree),
    seniority: addLabel(selectionList.seniority),
  };
};

const normalizeExpandedToAllSelectionLists = (
  selectionList: ExpandedSelectionListData
): AllSelectionLists => normalizeExpandedSelectionLists(selectionList);

/**
 * Fetch general selection lists from GraphQL
 */
export const fetchSelectionListsFromAPI = async (
  gqlClient: Client,
  useReferenceApi = false
): Promise<{ data: GqlSelectionLists | null; error?: Error }> => {
  try {
    const selectionListsPromise = gqlClient.query(
      SELECTION_LISTS,
      {},
      { priority: 'low' }
    );

    if (!useReferenceApi) {
      const { data, error } = await selectionListsPromise;
      if (error) {
        return { error, data: null };
      }
      const selectionList = data?.selectionList;
      if (!selectionList) return { data: null };
      return { data: legacySelectionListToGqlSelectionLists(selectionList) };
    }

    const referenceListsPromise = gqlClient.query(
      REFERENCE_LISTS_QUERY,
      {},
      {
        priority: 'low',
        skipGlobalErrorHandling: true,
      }
    );

    const [selectionListsResult, referenceListsResult] = await Promise.all([
      selectionListsPromise,
      referenceListsPromise,
    ]);

    if (selectionListsResult.error) {
      return { error: selectionListsResult.error, data: null };
    }
    if (referenceListsResult.error) {
      logReferenceListsError(referenceListsResult.error);
    }

    const selectionList = selectionListsResult.data?.selectionList;
    if (!selectionList) return { data: null };

    const referenceLists = normalizeReferenceLists(referenceListsResult.data);

    return {
      data: mergeGqlSelectionListsWithReferenceLists(
        legacySelectionListToGqlSelectionLists(selectionList),
        referenceLists
      ),
    };
  } catch (error) {
    console.error('Error fetching selection lists:', error);
    return {
      data: null,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
};

/**
 * Fetch expanded selection lists (includes all skill granularities + geo state) from GraphQL
 */
export const fetchExpandedSelectionListsFromAPI = async (
  gqlClient: Client,
  useReferenceApi = false
): Promise<AllSelectionLists | null> => {
  try {
    const expandedSelectionListsPromise = gqlClient.query(
      EXPANDED_SELECTION_LISTS,
      {},
      { priority: 'low' }
    );

    if (!useReferenceApi) {
      const { data, error } = await expandedSelectionListsPromise;
      if (error) {
        console.error('Error fetching selection lists:', error);
        return null;
      }

      return data?.selectionList
        ? normalizeExpandedToAllSelectionLists(data.selectionList)
        : null;
    }

    const referenceListsPromise = gqlClient.query(
      REFERENCE_LISTS_QUERY,
      {},
      {
        priority: 'low',
        skipGlobalErrorHandling: true,
      }
    );

    const [expandedSelectionListsResult, referenceListsResult] =
      await Promise.all([expandedSelectionListsPromise, referenceListsPromise]);
    const { data, error } = expandedSelectionListsResult;
    if (error) {
      console.error('Error fetching selection lists:', error);
      return null;
    }

    if (data?.selectionList) {
      const expandedSelectionLists = normalizeExpandedToAllSelectionLists(
        data.selectionList
      );
      if (referenceListsResult.error) {
        logReferenceListsError(referenceListsResult.error);
      }
      const referenceLists = normalizeReferenceLists(referenceListsResult.data);
      return mergeAllSelectionListsWithReferenceLists(
        expandedSelectionLists,
        referenceLists
      );
    }

    return data?.selectionList ?? null;
  } catch (error) {
    console.error('Error fetching selection lists:', error);
    return null;
  }
};
